import React, { useState, useEffect } from 'react';
import { FiSettings, FiTool, FiAlertTriangle } from 'react-icons/fi';
import PageHeader from '../../../components/ui/PageHeader';
import { toast } from 'react-toastify';
import { apiCall } from '../../../api/fetchClient';
import { GET_MAINTENANCE_URL, TOGGLE_MAINTENANCE_URL } from '../../../api/url';

const SettingsPage = () => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchMaintenanceStatus();
  }, []);

  const fetchMaintenanceStatus = async () => {
    try {
      const response = await apiCall(GET_MAINTENANCE_URL, 'GET', null, false, false);
      if (response.success && response.results) {
        // adjust based on actual API response structure
        setIsMaintenanceMode(response.results.isMaintenance);
      }
    } catch (error) {
      console.error('Failed to fetch maintenance status:', error);
    }
  };

  const handleToggleMaintenance = async () => {
    setIsLoading(true);
    const newStatus = !isMaintenanceMode;
    try {
      const response = await apiCall(TOGGLE_MAINTENANCE_URL, 'PUT', { isMaintenance: newStatus }, false, true);
      if (response.success) {
        setIsMaintenanceMode(newStatus);
      }
    } catch (error) {
      // The toast is handled in apiCall already (showToast=true)
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-12">
      <PageHeader
        title="Settings"
        description="Manage system configurations and application preferences."
        icon={FiSettings}
      />

      <div className="space-y-8">
        
        {/* Settings Group: System */}
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200/70 dark:border-slate-800 overflow-hidden">
          
          <div className="px-6 py-5 border-b border-gray-100 dark:border-slate-800/60 bg-gray-50/50 dark:bg-slate-800/20">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <FiSettings className="w-4 h-4" />
              System Configuration
            </h2>
          </div>
          
          <div className="p-6">
            
            {/* Maintenance Mode Option */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className={`p-2.5 mt-0.5 rounded-lg shrink-0 transition-colors ${
                  isMaintenanceMode 
                    ? 'bg-primary text-white dark:bg-primary-dark dark:text-white' 
                    : 'bg-gray-100 text-gray-500 dark:bg-slate-800 dark:text-gray-400'
                }`}>
                  <FiTool className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                    Maintenance Mode
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl leading-relaxed">
                    Temporarily disable user access to the application during critical updates. Super admins will still be able to log in.
                  </p>
                  
                  {/* Status Indicator */}
                  {isMaintenanceMode && (
                    <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-primary dark:text-primary-dark border border-primary/20 dark:border-primary-dark/20 text-xs font-medium transition-all animate-in fade-in slide-in-from-top-1">
                      <FiAlertTriangle className="w-3.5 h-3.5" />
                      Active (Users cannot access the site)
                    </div>
                  )}
                </div>
              </div>

              <div className="shrink-0 flex items-center pl-[52px] sm:pl-0">
                <button
                  type="button"
                  onClick={handleToggleMaintenance}
                  disabled={isLoading}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-slate-900 ${
                    isMaintenanceMode 
                      ? 'bg-primary hover:bg-primary-dark dark:bg-primary-dark dark:hover:bg-primary' 
                      : 'bg-gray-200 hover:bg-gray-300 dark:bg-slate-700 dark:hover:bg-slate-600'
                  } ${isLoading ? 'opacity-50 cursor-wait' : ''}`}
                  role="switch"
                  aria-checked={isMaintenanceMode}
                >
                  <span className="sr-only">Toggle maintenance mode</span>
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      isMaintenanceMode ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
            
          </div>
        </div>

      </div>
    </div>
  );
};

export default SettingsPage;
